import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { Download } from "lucide-react";
import { toast } from "sonner";

import { instance as axiosInstance } from "@/services/API";
import { setSearchType } from "@/state/searchInputSlice";
import { useIsBroker } from "@/auth/roles";
import { homepageRoute } from "@/routing";
import {
    ALLOWED_ACCEPT,
    ALLOWED_EXTENSIONS,
    BATCH_STATUS,
    BatchDropzone,
    BatchPredictionRow,
    MAX_FILE_SIZE_BYTES,
} from "@/components/dataPages/batch";

const getExtension = (name) => name.substring(name.lastIndexOf(".") + 1).toLowerCase();

const countCsvLines = (text) => {
    if (!text) return 0;
    const trimmed = text.replace(/\n+$/u, "");
    if (!trimmed) return 0;
    const total = trimmed.split(/\r?\n/u).length;
    return Math.max(0, total - 1);
};

export const Batch = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const canAccess = useIsBroker();
    const fileInputRef = useRef(null);
    const [predictions, setPredictions] = useState([]);
    const [uploadingIds, setUploadingIds] = useState(() => new Set());
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);
    const controllersRef = useRef(new Map());

    useEffect(() => {
        dispatch(setSearchType("batch"));
    }, [dispatch]);

    useEffect(() => {
        if (!canAccess) {
            navigate(homepageRoute, { replace: true });
        }
    }, [canAccess, navigate]);

    useEffect(() => {
        return () => {
            predictions.forEach((p) => {
                if (p.downloadUrl) URL.revokeObjectURL(p.downloadUrl);
            });
        };
    }, [predictions]);

    useEffect(() => {
        const controllers = controllersRef.current;
        return () => controllers.forEach((c) => c.abort());
    }, []);

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        const ext = getExtension(file.name);
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            toast.error("Incorrect format of the file.\nPlease upload the file in XLSX or CSV format.");
            return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            toast.error("File exceeds maximum size of 50 MB.");
            return;
        }

        const id = `${Date.now()}-${file.name}`;
        const createdAt = new Date();
        const controller = new AbortController();
        controllersRef.current.set(id, controller);
        setUploadingIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });

        let lineCount = null;
        if (ext === "csv") {
            try {
                lineCount = countCsvLines(await file.text());
            } catch {
                lineCount = null;
            }
        }

        const formData = new FormData();
        formData.append("file", file, file.name);

        const transitionToProcessing = () => {
            setUploadingIds((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            setPredictions((prev) =>
                prev.some((p) => p.id === id)
                    ? prev
                    : [{ id, fileName: file.name, extension: ext, status: BATCH_STATUS.PROCESSING, createdAt, lineCount }, ...prev],
            );
        };

        try {
            const response = await axiosInstance.post("/analytics/batch-price-estimate", formData, {
                responseType: "blob",
                signal: controller.signal,
                onUploadProgress: (event) => {
                    if (event.total && event.loaded >= event.total) transitionToProcessing();
                },
            });
            transitionToProcessing();
            const errorMessage = response.headers["x-batch-error"] ?? "";
            const blob = response.data;
            const downloadUrl = URL.createObjectURL(blob);
            const downloadName = `estimated_${file.name}`;

            setPredictions((prev) =>
                prev.map((p) =>
                    p.id === id
                        ? {
                              ...p,
                              status: errorMessage ? BATCH_STATUS.WARNING : BATCH_STATUS.DONE,
                              downloadUrl,
                              downloadName,
                              warning: errorMessage || undefined,
                          }
                        : p,
                ),
            );
        } catch (error) {
            const aborted = error?.name === "CanceledError" || error?.code === "ERR_CANCELED" || controller.signal.aborted;
            if (aborted) {
                setPredictions((prev) => prev.filter((p) => p.id !== id));
            } else {
                setPredictions((prev) =>
                    prev.map((p) => (p.id === id ? { ...p, status: BATCH_STATUS.FAILED, error: "Please try again." } : p)),
                );
            }
        } finally {
            controllersRef.current.delete(id);
            setUploadingIds((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    }, []);

    const handleCancel = (prediction) => {
        const controller = controllersRef.current.get(prediction.id);
        if (controller) controller.abort();
    };

    const handleDelete = (prediction) => {
        if (prediction.downloadUrl) URL.revokeObjectURL(prediction.downloadUrl);
        setPredictions((prev) => prev.filter((p) => p.id !== prediction.id));
    };

    const handleClickUpload = () => {
        if (uploadingIds.size > 0) return;
        fileInputRef.current?.click();
    };

    const onInputChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (file) handleFile(file);
    };

    const onDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current += 1;
        setIsDragging(true);
    };

    const onDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) setIsDragging(false);
    };

    const onDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const onDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);
        if (uploadingIds.size > 0) return;
        const file = event.dataTransfer?.files?.[0];
        if (file) handleFile(file);
    };

    const showRecent = predictions.length > 0;
    const isUploading = uploadingIds.size > 0;

    if (!canAccess) return null;

    return (
        <div className="w-full max-w-[1200px] mx-auto flex flex-col gap-6 py-6 px-2">
            <div className="flex flex-col gap-1">
                <h1 className="text-cm-p-text-primary text-[22px] font-medium leading-tight">Batch prediction</h1>
                <p className="text-cm-p-text-secondary text-sm">
                    Predict spot rates for bonus and Forecast contract prices at once by uploading a file
                </p>
            </div>
            <BatchDropzone
                isUploading={isUploading}
                isDragging={isDragging}
                onClick={handleClickUpload}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={onDrop}
            />
            <input ref={fileInputRef} type="file" accept={ALLOWED_ACCEPT} className="hidden" onChange={onInputChange} />
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4">
                    <p className={showRecent ? "text-cm-p-text-primary text-base font-medium" : "text-cm-p-text-secondary text-sm"}>
                        {showRecent ? "Recent batch predictions" : "Once you upload files, rate predictions will show up here"}
                    </p>
                    <a
                        href="/files/batch_price_estimate_example.csv"
                        download
                        className="flex items-center gap-2 px-1 text-cm-p-text-primary text-sm font-medium whitespace-nowrap hover:opacity-80 transition-opacity"
                    >
                        <Download className="size-4 text-cm-p-accent-600" />
                        Sample CSV
                    </a>
                </div>
                {showRecent ? (
                    <div className="flex flex-col gap-2">
                        {predictions.map((prediction) => (
                            <BatchPredictionRow
                                key={prediction.id}
                                prediction={prediction}
                                onCancel={handleCancel}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
